import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import MealPlansContext from '../../context/meal-plans-context';
import Loader from '../../components/UI/General/Loader';
import MealPlan from '../../models/meal-plan';

const MealPlansPage = () => {
  const { plans, isLoading, deletePlan } = useContext(MealPlansContext);
  const [showCompleted, setShowCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const visiblePlans = showCompleted ? plans : plans.filter((p) => p.status === 'active');

  const isOverdue = (plan: MealPlan): boolean => {
    return plan.status === 'active' && plan.dueDate !== null && plan.dueDate < new Date();
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await deletePlan(id);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Jídelní plány</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/meal-plan/new')}>
          Nový plán
        </Button>
      </Stack>

      <FormControlLabel
        control={<Switch checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
        label="Zobrazit dokončené"
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {visiblePlans.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          {showCompleted ? 'Nemáte žádné jídelní plány' : 'Nemáte žádné aktivní plány'}
        </Typography>
      )}

      <Grid container spacing={2}>
        {visiblePlans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card
              sx={{
                borderLeft: isOverdue(plan) ? '4px solid' : 'none',
                borderColor: isOverdue(plan) ? 'error.main' : 'transparent',
              }}
            >
              <CardActionArea onClick={() => navigate(`/meal-plan/${plan.id}`)}>
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {plan.dueDate ? `Termín: ${plan.dueDate.toLocaleDateString('cs-CZ')}` : 'Bez termínu'}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip label={`${plan.items.length} jídel`} size="small" variant="outlined" />
                    <Chip
                      label={plan.status === 'active' ? 'Aktivní' : 'Dokončený'}
                      size="small"
                      color={plan.status === 'active' ? 'primary' : 'default'}
                    />
                    {isOverdue(plan) && <Chip label="Po termínu" size="small" color="error" />}
                  </Stack>
                </CardContent>
              </CardActionArea>
              <Box sx={{ px: 2, pb: 1 }}>
                <Button
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(plan.id);
                  }}
                >
                  Smazat
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default MealPlansPage;
